import { useEffect } from 'react';

import loadable from '@loadable/component';
import i18n from 'i18next';
import { Navigate, Outlet, createBrowserRouter } from 'react-router';

import ModalConductor from '@/components/organisms/Modals/ModalConductor';
import Account from '@/components/pages/Account/Account';
import Accounts from '@/components/pages/Accounts/Accounts';
import AuthError from '@/components/pages/AuthError/AuthError';
import CreateAccount from '@/components/pages/CreateAccount/CreateAccount';
import Login from '@/components/pages/Login/Login';
import Logout from '@/components/pages/Logout/Logout';
import NotFound from '@/components/pages/NotFound/NotFound';
import ProfileRouter from '@/components/pages/Profile/ProfileRouter';
import Unauthorized from '@/components/pages/Unauthorized/Unauthorized';
import ErrorBoundary from '@/components/templates/ErrorBoundary';
import RequireAuth from '@/components/templates/RequireAuth';
import { useBeamer } from '@/hooks/useBeamer';
import useIframeStyles from '@/hooks/useIframeStyles';
import useMembers from '@/hooks/useMembers';
import { useMobileWarning } from '@/hooks/useMobileWarning';
import { useNavigatorOnLine } from '@/hooks/useNavigatorOnline';
import { usePreferences } from '@/hooks/usePreferences';
import { useRoles } from '@/hooks/useRoles';
import { useBillingState } from '@/modules/billing/hooks/useBillingState';
import { InvitationRouter } from '@/modules/invitation/InvitationRouter';
import { useSegmentIdentity } from '@/segment/useSegmentIdentity';
import { applyTheme, resolveTheme } from '@/util/theme';

const Onboarding = loadable(() => import('@/modules/onboarding/router'));
const Welcome = loadable(() => import('@/modules/welcome/router'));

// Language - Only allow UI-supported languages (en, es, pt-br) for main interface.
// Brain languages (de, fr, el, etc.) are loaded on-demand in specific components.
const UI_LANGUAGES = ['en', 'es', 'pt-br'];
// Public layout - no authentication required
const PublicLayout = () => (
  <ErrorBoundary>
    <Outlet />
  </ErrorBoundary>
);

// Protected layout - requires authentication
const ProtectedLayout = () => {
  const { userPreferences } = usePreferences();
  // Called for their fetches, which prime the caches these routes render from.
  useBillingState();
  useMembers();
  useRoles();
  useBeamer();

  useNavigatorOnLine();

  useIframeStyles();

  useMobileWarning();

  useSegmentIdentity();

  const diffLanguage = userPreferences?.language !== i18n.language;
  useEffect(() => {
    if (userPreferences?.language) {
      const userLang = String(userPreferences.language);
      // Validate that the user's language preference is a supported UI language
      if (UI_LANGUAGES.includes(userLang)) {
        i18n.changeLanguage(userLang);
      } else {
        // User preference is for a brain language, not a UI language - use English
        i18n.changeLanguage('en');
      }
    }
  }, [diffLanguage, userPreferences?.language]);

  // Apply only after prefs load to preserve the pre-paint theme.
  useEffect(() => {
    if (userPreferences?.theme) {
      applyTheme(resolveTheme(userPreferences.theme));
    }
  }, [userPreferences?.theme]);

  return (
    <ErrorBoundary>
      <Outlet />
      <ModalConductor />
    </ErrorBoundary>
  );
};

const routes = [
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      // Public routes - no authentication required
      { path: 'login', element: <Login /> },
      { path: 'logout', element: <Logout /> },
      { path: 'unauthorized', element: <Unauthorized /> },
      { path: 'auth-error', element: <AuthError /> },
      { path: 'invitation/*', element: <Navigate to="/invitations" replace /> },
      { path: 'invitations/*', element: <InvitationRouter /> },
      // Protected routes - authentication required via RequireAuth
      {
        element: <RequireAuth />,
        children: [
          {
            element: <ProtectedLayout />,
            children: [
              { index: true, element: <Accounts /> },
              { path: 'create-account', element: <CreateAccount /> },
              { path: 'welcome/*', element: <Welcome /> },
              { path: 'onboarding/*', element: <Onboarding /> },
              { path: 'not-found', element: <NotFound /> },
              { path: 'profile/*', element: <ProfileRouter /> },
              { path: ':slug/*', element: <Account /> },
            ],
          },
        ],
      },
      // Catch-all route for truly unmatched paths
      { path: '*', element: <NotFound /> },
    ],
  },
];

// According to https://reactrouter.com/en/main/routers/create-browser-router
// This is the recommended router for all React Router web projects.
// It uses the DOM History API to update the URL and manage the history stack.
export const browserRouter = createBrowserRouter(routes);
